import React, { useRef, useState, useEffect } from 'react';
import { Camera, X, CheckCircle, RotateCw, Upload } from 'lucide-react';

interface CameraCaptureProps {
  onCapture: (file: File) => void;
  onCancel: () => void;
  documentLabel: string;
}

export const CameraCapture: React.FC<CameraCaptureProps> = ({
  onCapture,
  onCancel,
  documentLabel,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [cameraActive, setCameraActive] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');

  useEffect(() => {
    startCamera();

    return () => {
      stopCamera();
    };
  }, [facingMode]);

  const startCamera = async () => {
    try {
      setError('');

      // Stop existing stream if any
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      // Request camera access
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });

      setStream(mediaStream);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.play();
        setCameraActive(true);
      }
    } catch (err: any) {
      console.error('Camera error:', err);
      if (err.name === 'NotAllowedError') {
        setError('Camera access denied. Please allow camera access to capture documents.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera found on this device.');
      } else {
        setError('Failed to access camera. You can use file upload instead.');
      }
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
      setCameraActive(false);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    const context = canvas.getContext('2d');
    if (context) {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (blob) {
          const imageUrl = canvas.toDataURL('image/jpeg', 0.9);
          setCapturedImage(imageUrl);
          stopCamera();
        }
      }, 'image/jpeg', 0.9);
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    startCamera();
  };

  const switchCamera = () => {
    setFacingMode(facingMode === 'user' ? 'environment' : 'user');
  };

  const confirmCapture = () => {
    if (!canvasRef.current || !capturedImage) return;

    canvasRef.current.toBlob((blob) => {
      if (blob) {
        // Create file from blob
        const timestamp = Date.now();
        const file = new File(
          [blob],
          `${documentLabel.replace(/\s+/g, '_')}_${timestamp}.jpg`,
          { type: 'image/jpeg' }
        );
        onCapture(file);
      }
    }, 'image/jpeg', 0.9);
  };

  return (
    <div className="camera-capture-modal">
      <div className="camera-overlay">
        <div className="camera-container">
          <div className="camera-header">
            <h3>Capture {documentLabel}</h3>
            <button onClick={onCancel} className="close-button">
              <X size={24} />
            </button>
          </div>

          {error && (
            <div className="camera-error">
              <AlertCircle size={20} />
              <p>{error}</p>
            </div>
          )}

          <div className="camera-view">
            {!capturedImage ? (
              <>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="camera-video"
                />

                {/* Document framing guide overlay */}
                <div className="document-frame-guide">
                  <div className="frame-corner top-left"></div>
                  <div className="frame-corner top-right"></div>
                  <div className="frame-corner bottom-left"></div>
                  <div className="frame-corner bottom-right"></div>
                  <div className="frame-instructions">
                    Position document within frame
                  </div>
                </div>
              </>
            ) : (
              <img src={capturedImage} alt="Captured document" className="captured-image" />
            )}

            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>

          <div className="camera-controls">
            {!capturedImage ? (
              <>
                {cameraActive && (
                  <>
                    <button onClick={switchCamera} className="btn btn-secondary btn-icon">
                      <RotateCw size={20} />
                      <span>Flip Camera</span>
                    </button>
                    <button onClick={capturePhoto} className="btn btn-primary btn-capture">
                      <Camera size={28} />
                      <span>Capture</span>
                    </button>
                  </>
                )}
                <button onClick={onCancel} className="btn btn-secondary">
                  Use File Upload
                </button>
              </>
            ) : (
              <>
                <button onClick={retakePhoto} className="btn btn-secondary">
                  <RotateCw size={20} />
                  <span>Retake</span>
                </button>
                <button onClick={confirmCapture} className="btn btn-success">
                  <CheckCircle size={20} />
                  <span>Use This Photo</span>
                </button>
              </>
            )}
          </div>

          <div className="camera-tips">
            <p><strong>Tips for best results:</strong></p>
            <ul>
              <li>Ensure good lighting</li>
              <li>Place document on flat surface</li>
              <li>Capture all corners of document</li>
              <li>Avoid shadows and glare</li>
              <li>Ensure text is readable</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Fallback component for browsers without camera support
const AlertCircle: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <circle cx="12" cy="12" r="10" strokeWidth="2" />
    <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2" />
    <line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2" />
  </svg>
);
